
	import { Component, Vue } from "vue-property-decorator";
	import { ValidationObserver, ValidationProvider } from "vee-validate";
	import ImageUpload from "@/components/inputs/ImageUpload.vue";
	import ExtraServiceTypeSelect from "@/components/inputs/ExtraServiceTypeSelect.vue";
	import ActionConfirmation from "@/components/modals/ActionConfirmation.vue";
	import BulkTransportTypes from "@/models/bulk_transport_types";
	import BulkExtraServiceTypes from "@/models/bulk_extra_service_types";
	import TransportType from "@/models/transport_type";
	import ServiceType from "@/models/service_type";
	import TransportTypeService from "@/services/TransportTypeService";
	import ApiHelper from "@/helpers/api-helper";
	import ServicesTypeService from "@/services/ServicesTypeService";
	import i18n from '@/plugins/i18n';

	@Component({
		components: {
			ValidationProvider,
			ValidationObserver,
			ImageUpload,
			ExtraServiceTypeSelect,
			ActionConfirmation,
		},
	})
	export default class TypesConfiguration extends Vue {
		$refs!: {
			obsTransportes: InstanceType<typeof ValidationObserver>;
			obsServicios: InstanceType<typeof ValidationObserver>;
			quitar: ActionConfirmation;
		};

		private transports: any;
		private services: any;
		private pagination: any;
		private filters: any;
		/** N-291 (7A): dentro de qué estaba cada tipo al pedir la lista (id → padre). Agrupa la pantalla. */
		private padresAlCargar: any;
		/** N-291 (7A): la fila que la papelera quiere quitar, hasta que se confirme. */
		private aQuitar: any;
		private textoQuitar: string;
		private filasNuevas: number;

		constructor() {
			super();
			this.transports = {};
			this.transports.transport_types = [];
			this.services = {};
			this.services.extra_service_types = [];
			this.pagination = null;
			this.filters = {};
			this.padresAlCargar = {};
			this.aQuitar = null;
			this.textoQuitar = '';
			this.filasNuevas = 0;
		}

		/**
		 * N-291 (7A): las categorías principales, sacadas de la lista ya pedida (antes, cada fila pedía
		 * la suya: 22 peticiones iguales al abrir). Como lo guardado, igual que el servidor: lo que se
		 * cambie se ve al Guardar.
		 */
		get principales() {
			return this.services.extra_service_types
				.filter((tipo) => tipo.id && !this.padresAlCargar[tipo.id])
				.map((tipo) => ({ id: tipo.id, name: tipo.name }));
		}

		/**
		 * N-291 (7A): cada categoría principal y debajo sus tipos, en el orden de la lista. Se agrupa por
		 * lo guardado, así las filas no saltan mientras se cambia «Dentro de». Al final, las filas nuevas
		 * (y cualquier tipo cuya categoría no esté en la lista).
		 */
		get filasServicios() {
			const tipos = this.services.extra_service_types;
			const filas: any[] = [];
			const fila = (servicio, hija) => ({ servicio, hija, clave: servicio.id || 'nueva-' + servicio.nueva });
			tipos
				.filter((tipo) => tipo.id && !this.padresAlCargar[tipo.id])
				.forEach((principal) => {
					filas.push(fila(principal, false));
					tipos
						.filter((tipo) => tipo.id && this.padresAlCargar[tipo.id] === principal.id)
						.forEach((hija) => filas.push(fila(hija, true)));
				});
			tipos
				.filter((tipo) => !filas.some((puesta) => puesta.servicio === tipo))
				.forEach((resto) => filas.push(fila(resto, !!resto.extra_service_type_id)));
			return filas;
		}

		sinNombre(servicio) {
			return !!servicio.id && !(servicio.name || '').trim();
		}

		mounted() {
			this.listTransport();
			this.listService();
		}

		listTransport() {
			TransportTypeService.list(this.pagination, this.filters)
				.then((res) => {
					if (res.status === 200) {
						this.transports.transport_types = res.data.elements;
					}
				})
				.catch((error) => {
					ApiHelper.handleErrors(error, {}, this.$bus);
				});
		}

		bulkUpdateTransport() {
			const component = this;
			TransportTypeService.bulkUpdate(this.transports)
				.then((res) => {
					if (res.status === 200) {
						if(res.data.allSelectedDeleted) {
							this.$bus.$emit('success', {message: i18n.t('success_saved_message')});
						} else {
							this.$bus.$emit('error', { message: i18n.t('transport_types_with_transports')});
						}
						this.listTransport();
					}
				})
				.catch((error) => {
					ApiHelper.handleErrors(error, component.$refs.obsTransportes, this.$bus);
				});
		}

		addTransportRow() {
			this.transports.transport_types.push(new TransportType());
			return false;
		}

		deleteTransport(transport) {
			this.pedirQuitar(this.transports.transport_types, transport, 'Si lo usa algún transporte, no se borrará.');
		}

		listService() {
			ServicesTypeService.list(this.pagination, this.filters)
				.then((res) => {
					if (res.status === 200) {
						const padres = {};
						res.data.elements.forEach((tipo) => {
							padres[tipo.id] = tipo.extra_service_type_id;
						});
						this.padresAlCargar = padres;
						this.services.extra_service_types = res.data.elements;
					}
				})
				.catch((error) => {
					ApiHelper.handleErrors(error, {}, this.$bus);
				});
		}

		bulkUpdateService() {
			const component = this;
			ServicesTypeService.bulkUpdate(this.services)
				.then((res) => {
					if (res.status === 200) {
						if(res.data.allSelectedDeleted) {
							this.$bus.$emit('success', {message: i18n.t('success_saved_message')});
						} else {
							this.$bus.$emit('error', { message: i18n.t('service_types_with_prices')});
						}
						this.listService();
					}
				})
				.catch((error) => {
					ApiHelper.handleErrors(error, component.$refs.obsServicios, this.$bus);
				});
		}

		addServicesRow() {
			const nueva: any = new ServiceType();
			// Solo para la clave de la fila en pantalla (las nuevas aún no tienen id).
			Object.defineProperty(nueva, 'nueva', { value: ++this.filasNuevas, enumerable: false });
			this.services.extra_service_types.push(nueva);
			return false;
		}

		deleteService(service) {
			this.pedirQuitar(this.services.extra_service_types, service, 'Si tiene tipos dentro o tarifas, no se borrará.');
		}

		/**
		 * N-291 (7A): la papelera pregunta antes de quitar la fila. El borrado de verdad es al Guardar,
		 * y el servidor no borra lo que se usa (`bulkUpdate`): por eso lo dice.
		 */
		pedirQuitar(lista, fila, siSeUsa) {
			this.aQuitar = { lista, fila };
			if (!fila.id) {
				this.textoQuitar = '¿Quitar esta fila nueva? Aún no está guardada.';
			} else {
				const nombre = (fila.name || '').trim() || 'sin nombre';
				this.textoQuitar = `¿Quitar «${nombre}»? Se borra al pulsar Guardar. ${siSeUsa}`;
			}
			this.$refs.quitar.show();
		}

		quitarConfirmado() {
			if (this.aQuitar) {
				const { lista, fila } = this.aQuitar;
				const index = lista.indexOf(fila);
				if (index > -1) {
					lista.splice(index, 1);
				}
			}
			this.aQuitar = null;
			this.$refs.quitar.close();
		}

		async validateFile($event, index) {
			await this.$refs["img_url"][index].validate($event);
		}

		isImageUploaded(img) {
			return typeof img === "string";
		}
	}
